<script lang="ts" setup>
const { t: $t } = useI18n()

import { formatDistanceToNowStrict } from 'date-fns'
import { de } from 'date-fns/locale'
import type { ApiCommentGetAll, CreateComment, UpdateOrCreateComment } from '~/types'
import type { ApiCustomerModuleCommentGetAll } from '~/layers/customer-module/types'
import { type CommentReactionType, CommentType, CommentVisibility } from '~/prisma/enums'

const props = defineProps<{
  comment: ApiCommentGetAll | ApiCustomerModuleCommentGetAll
  isReadOnly: boolean
  isDeleteDisabled: boolean
  isVisibilityAdjustable: boolean
  entityId: string
  isReplyDisabled?: boolean
  isCreating?: boolean
}>()
const emits = defineEmits<{
  (e: 'create', payload: CreateComment['data']): void
  (e: 'update', commentId: string): void
  (e: 'delete', commentId: string): void
  (e: 'reactionUpsert', commentId: string, type: CommentReactionType): void
  (e: 'reactionDelete', commentId: string): void
  (e: 'reply', { isOpen, comment }: { isOpen: boolean, comment: ApiCommentGetAll }): void
}>()

const { useremail, hasAnyAssignedRole } = useAuthorization()
const isCustomerUserComment = computed(() => hasAnyRole(props.comment.user.roles, ['customerUser']))
const isAuthorOfComment = computed(() => useremail.value === props.comment.user.email)
const isCustomerUser = hasAnyAssignedRole(['customerUser'], false)
const isPrivileged = hasAnyAssignedRole(['admin', 'specialRoleBenni'])
const isVisibleToSpecificUsers = computed(() => props.comment.visibleTo.length > 0)

const { userOptions, isSupportRestrictedToSpecificUser } = useCommentVisibleToOptions(
  ref(props.comment.type),
  ref(props.entityId),
)

const reactionsByType = computed(() => Object.groupBy(props.comment.reactions, reaction => reaction.type))
const userSelectedReaction = computed(() => props.comment.reactions.find(reaction => reaction.createdBy.email === useremail.value))
const commentTitle = computed(() => {
  if (props.comment.deletedAt) {
    return undefined
  }

  const title = props.comment.user.name
  if (isCustomerUserComment.value && !isCustomerUser.value) {
    if (props.comment.isOfferCancellation) {
      return `[${$t('offer.rental.cancellationReason.option.CustomerModule.offerCancelledComment')}] ${title}`
    }
    return `[${$t('comment.customer.label')}] ${title}`
  }
  return title
})

const replyFormRef = ref<HTMLElement | null>(null)
const replyFormPayload = ref<UpdateOrCreateComment | null>(null)
const showReplyForm = ref<boolean>(false)

function generateCommentText(payload: { text?: string | null, users: { email: string, name: string | null }[] }) {
  const elements: string[] = []

  if (payload.text) {
    elements.push(`<blockquote>${payload.text}</blockquote>`)
  }

  if (payload.users.length > 0) {
    const userElements = payload.users.map((user) => {
      const dataLabel = user.name ?? user.email

      return `<span class="Mention" data-type="mention" data-id="${user.email}" data-label="${dataLabel}">@${dataLabel}</span>`
    })
    elements.push(`<p>${userElements.join(' ')}</p>`)
  }

  if (payload.users.length === 0) {
    elements.push('&nbsp;')
  }

  return elements.join('')
}

function makeReplyFormPayload(comment: ApiCommentGetAll, mention: 'all' | 'author' = 'author') {
  const commentType = props.comment.type
  const commentEntityId = commentConfigs[commentType].commentTableRelationFieldName

  const canMentionUsers = !isCustomerUser.value
    && !isCustomerUserComment.value
    && CommentType.ServiceProjectReporting !== commentType
    && CommentType.OfferPreparation !== commentType

  const usersToMention = new Map([[comment.user.email, comment.user]])
  if (mention === 'all') {
    comment.replies.forEach(({ user }) => usersToMention.set(user.email, user))
  }

  /**
   * NOTE: Auto-mention of original/parent commenter is disabled when:
   * - current user is a customer user
   * - original/parent commenter is a customer user
   * - comment type is ServiceProjectReporting
   */
  const originalCommentMention = generateCommentText({
    text: mention === 'author' ? comment.text : null,
    users: canMentionUsers ? Array.from(usersToMention.values()) : [],
  })

  const visibleToIds = new Set<string>()
  if (props.comment.visibleTo.length > 0) {
    props.comment.visibleTo.forEach(({ id }) => visibleToIds.add(id))
  }

  return {
    mode: 'create' as const,
    data: {
      subject: comment.subject,
      text: originalCommentMention,
      type: commentType,
      visibility: isCustomerUser.value || commentType === CommentType.ServiceProjectReporting || commentType === CommentType.PartnerSaleReservation || commentType === CommentType.OfferPreparation ? CommentVisibility.Public : CommentVisibility.Private,
      visibleToIds: [...visibleToIds],
      originalCommentId: props.comment.id,
      [commentEntityId]: props.entityId,
      files: [],
    },
  }
}

async function toggleReplyForm(isOpen?: boolean, comment?: ApiCommentGetAll, mention?: 'all' | 'author') {
  showReplyForm.value = isOpen ?? !showReplyForm.value
  if (!showReplyForm.value) {
    return
  }

  replyFormPayload.value = makeReplyFormPayload(comment ?? props.comment, mention)

  // Await 500ms to ensure that the form and Editor can render
  await sleep(500)
  if (showReplyForm.value && replyFormRef.value) {
    replyFormRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  }
}

function handleReactionClick(type: CommentReactionType) {
  if (type === userSelectedReaction.value?.type) {
    emits('reactionDelete', props.comment.id)
  } else {
    emits('reactionUpsert', props.comment.id, type)
  }
}

function createComment(payload: UpdateOrCreateComment) {
  // We only send `mode === create` so it can't be `update`
  // But we add this check for type safety
  if (payload.mode === 'create') {
    emits('create', payload.data)
  }
  toggleReplyForm(false)
}

const showPublicCommentWarning = computed(() => {
  return props.comment.visibility === CommentVisibility.Public
    && !isCustomerUser.value
    && props.comment.type !== CommentType.OfferPreparation
})
</script>

<template>
  <n-card v-if="comment.deletedAt">
    {{ $t('comment.deleted.label') }}
    <n-divider v-if="comment.totalRepliesCount > 0" />
    <CommentReplyContainer
      :comment
      :entity-id
      :is-read-only
      :is-delete-disabled
      :is-visibility-adjustable
      @create="emits('create', $event)"
      @update="emits('update', $event)"
      @delete="emits('delete', $event)"
      @reaction-upsert="(id, type) => emits('reactionUpsert', id, type)"
      @reaction-delete="emits('reactionDelete', $event)"
    />
  </n-card>
  <div v-else>
    <div
      class="p-3 rounded bg-gray-50 border"
      :class="{
        'border-t-8 border-t-blue-300': isVisibleToSpecificUsers,
        'rounded-b-none border-b-0': (isVisibleToSpecificUsers || showPublicCommentWarning),
        'bg-green-50': isAuthorOfComment,
      }"
    >
      <div class="flex items-center justify-between gap-4">
        <h3 class="flex flex-col sm:flex-row gap-1">
          <div class="flex items-center gap-1 text-sm">
            <span>
              {{ commentTitle }}
            </span>
            <span>
              •
            </span>
            <n-tooltip trigger="hover">
              <template #trigger>
                <time :datetime="comment.createdAt.toDateString()" class="text-gray-400">
                  {{ formatDistanceToNowStrict(comment.createdAt, { addSuffix: true, locale: de }) }}
                </time>
              </template>
              {{ useDateAsString(comment.createdAt) }}
            </n-tooltip>
          </div>
          <div v-if="comment.subject" class="flex gap-1 text-sm">
            <span class="hidden sm:inline">
              •
            </span>
            <span>
              {{ comment.subject }}
            </span>
          </div>
        </h3>

        <div class="flex flex-col sm:flex-row justify-end gap-1">
          <n-button
            v-if="isAuthorOfComment && !isReadOnly"
            type="primary"
            tertiary
            size="small"
            @click="emits('update', comment.id)"
          >
            <template #icon>
              <Icon name="material-symbols:edit-square-outline-rounded" />
            </template>
          </n-button>
          <n-button
            v-if="(isAuthorOfComment || isPrivileged) && !isDeleteDisabled"
            type="error"
            tertiary
            size="small"
            @click="emits('delete', comment.id)"
          >
            <template #icon>
              <Icon name="material-symbols:delete-outline-rounded" />
            </template>
          </n-button>
        </div>
      </div>

      <div class="flex flex-col">
        <!-- HTML is sanitized before being rendered  -->
        <CommentTextRender v-if="comment.text" :text="comment.text" />
        <FileView
          v-if="comment.files.length"
          :files="comment.files"
          collapsed-by-default
          class="mb-5"
        />

        <div class="flex justify-between items-center">
          <div class="flex items-center gap-1">
            <CommentReactionPopover
              v-if="!isReadOnly"
              :user-selected-reaction
              @handle-reaction-click="handleReactionClick"
            />
            <CommentReactionList
              :is-read-only
              :reactions-by-type
              :user-selected-reaction
              @handle-reaction-click="handleReactionClick"
            />
          </div>
          <n-button
            v-if="!isReadOnly"
            secondary
            :disabled="showReplyForm || isReplyDisabled"
            size="small"
            @click="() => comment.originalCommentId === null ? toggleReplyForm() : $emit('reply', { isOpen: true, comment })"
          >
            <template #icon>
              <Icon name="material-symbols:reply-rounded" />
            </template>
          </n-button>
        </div>
      </div>
    </div>

    <div v-if="showPublicCommentWarning" :class="isAuthorOfComment ? 'bg-green-100' : 'bg-gray-100'" class="p-2 rounded-b border border-t-0">
      <span class="text-gray-500">
        {{ $t('comment.item.visibility.public.label') }}
      </span>
    </div>

    <div v-if="isVisibleToSpecificUsers" class="bg-gray-100 p-2 rounded-b border border-t-0">
      <span class="text-gray-500">
        {{ $t('comment.item.visibleTo.label') }}: {{ comment.visibleTo.map(user => user.name ?? $t('user.name.unknown')).join(', ') }}
      </span>
    </div>

    <div class="space-y-2 ml-2 sm:ml-10 mr-1">
      <CommentReplyContainer
        :comment
        :entity-id
        :is-read-only
        :is-delete-disabled
        :is-visibility-adjustable
        :show-reply-form="showReplyForm || isCreating"
        @create="emits('create', $event)"
        @update="emits('update', $event)"
        @delete="emits('delete', $event)"
        @reaction-upsert="(id, type) => emits('reactionUpsert', id, type)"
        @reaction-delete="emits('reactionDelete', $event)"
        @reply="({ isOpen, comment }) => toggleReplyForm(isOpen, comment)"
      >
        <TransitionGroup name="CommentReply" mode="out-in">
          <div v-if="(showReplyForm || isCreating) && replyFormPayload" class="p-1">
            <n-spin :show="isCreating">
              <CommentCreateOrEditForm
                :is-visibility-adjustable
                editor-height="10vh"
                :payload="replyFormPayload"
                :user-options
                :is-support-restricted-to-specific-user
                is-reply
                @save="createComment"
                @close="toggleReplyForm(false)"
              />
              <div ref="replyFormRef" />
            </n-spin>
          </div>
          <div v-else class="flex flex-col sm:flex-row justify-end gap-2">
            <n-button size="small" secondary @click="toggleReplyForm()">
              <template #icon>
                <Icon name="material-symbols:reply-rounded" />
              </template>
              {{ $t('comment.reply.label') }}
            </n-button>
            <n-button size="small" secondary @click="toggleReplyForm(true, comment, 'all')">
              <template #icon>
                <Icon name="material-symbols:reply-all" />
              </template>
              {{ $t('comment.replyAll.label') }}
            </n-button>
          </div>
        </TransitionGroup>
      </CommentReplyContainer>
    </div>
  </div>
</template>

<style scoped>
:deep(.n-collapse .n-collapse-item .n-collapse-item) {
  @apply !ml-0;
}

/* Comment reply transition */
.CommentReply-enter-active, .CommentReply-leave-active {
  transition: max-height 0.3s ease-in-out, opacity 0.2s ease-in-out;
  overflow: hidden;
}
.CommentReply-enter-from, .CommentReply-leave-to {
  max-height: 0;
  opacity: 0;
}
.CommentReply-enter-to, .CommentReply-leave-from {
  max-height: 500px;
  opacity: 1;
}
:deep(blockquote:not(blockquote blockquote)) {
  @apply w-full max-h-52 p-2 overflow-auto;
}
</style>
